import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import EnquiryForm from '../components/EnquiryForm';
import CountryCard from '../components/CountryCard';
import FaqItem from '../components/FaqItem';
import RevealProvider from '../components/RevealProvider';
import { COUNTRIES, formatUSD } from '../data/countries';
import { latestPosts, formatDate } from '../data/posts';
import { useCountryOverride } from '../hooks/useOverrides';

const REGION_FILTERS = [
  { key: 'all', label: 'All regions' },
  { key: 'europe', label: 'Europe' },
  { key: 'asia', label: 'Asia' },
  { key: 'americas', label: 'Americas' },
  { key: 'middle_east', label: 'Middle East' },
  { key: 'africa', label: 'Africa' },
];

const FAQS = [
  { q: 'How much does it cost?', a: 'Our flat fee starts at $500 per application and covers document preparation, income verification, translation coordination, and submission to the embassy or consulate. Government fees, apostilles, and translations are billed at cost. No hourly surprises. Full pricing is shared in your first call.' },
  { q: 'How long does the whole process take?', a: 'From first call to visa in hand, most applications take between 8 and 20 weeks depending on country. Portugal’s D8 is typically 16 weeks. Dubai’s virtual working programme can be as quick as 1 week. We give you a realistic timeline in our first call, not a sales pitch.' },
  { q: 'Can you help if I have a spouse and kids?', a: 'Yes — this is one of the reasons our clients choose us. We know the spouse income thresholds for every country we cover, the dependent visa requirements, international school landscape, and which countries treat families well (and which don’t). Family-aware advice is built into how we work.' },
  { q: 'What if my visa gets rejected?', a: 'We only take cases we believe we can win. If an application is unclear or borderline, we tell you before you pay anything. In the rare event of a rejection for a case we accepted, we’ll resubmit or refile at no additional service fee.' },
  { q: 'Are you a law firm?', a: 'We’re a visa assistance service, not a law firm. We work alongside a network of licensed immigration lawyers and tax advisors in each country, and we’ll introduce you to them when a case needs legal representation. For standard digital nomad visa applications, legal representation is usually not required.' },
  { q: 'How is Nomad Compass different from Citizen Remote or other platforms?', a: 'Most platforms are marketplaces or directories — they hand you off to a third-party lawyer and take a commission. We handle your case ourselves, end to end. One expert. One fee. One point of contact. No markup. No handoffs.' },
];

export default function Home(){
  // Pick random country for hero card on each full page load (persists through session for this view only)
  const heroCountry = useMemo(() => COUNTRIES[Math.floor(Math.random() * COUNTRIES.length)], []);
  const heroOverride = useCountryOverride(heroCountry.slug);
  const heroImage = heroOverride.hero || heroCountry.image;

  const [region, setRegion] = useState('all');
  const featured = useMemo(() => {
    let base = region === 'all'
      ? ['portugal','spain','united-arab-emirates','croatia','costa-rica','indonesia','mexico','japan']
      : COUNTRIES.filter(c => c.region === region).slice(0, 8).map(c => c.slug);
    return base.map(slug => COUNTRIES.find(c => c.slug === slug)).filter(Boolean);
  }, [region]);

  useEffect(() => {
    // Smooth-scroll to hash after mount (e.g. /#enquiry)
    if (window.location.hash){
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, []);

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Nomad Compass",
    "url": "https://nomadcompasshub.com/",
    "description": "Digital nomad visa assistance across 40+ countries.",
    "email": "hello@nomadcompasshub.com",
    "sameAs": []
  };
  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Nomad Compass",
    "url": "https://nomadcompasshub.com/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://nomadcompasshub.com/countries?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <RevealProvider>
      <Helmet>
        <title>Nomad Compass — Digital Nomad Visa Assistance | Apply With Expert Help</title>
        <meta name="description" content="We help digital nomads apply for visas in 40+ countries. From paperwork to approval — expert guidance, clear pricing, human service." />
        <link rel="canonical" href="https://nomadcompasshub.com/" />
        <meta property="og:title" content="Nomad Compass — Digital Nomad Visa Assistance" />
        <meta property="og:description" content="One flat fee. One expert. Start-to-approval visa assistance across 40+ countries." />
        <meta property="og:url" content="https://nomadcompasshub.com/" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=1200&q=80" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      </Helmet>

      <TopBar variant="home" />

      <section className="hero">
        <div className="hero-text reveal">
          <div className="eyebrow">Digital Nomad Visa Assistance</div>
          <h1 className="hero-title">Moving abroad is<br/>complex. <em>Your visa<br/>shouldn’t be.</em></h1>
          <p className="hero-sub">We handle digital nomad visa applications from start to approval — documents, income verification, translations, embassy filing. One flat fee. One dedicated expert. No guesswork.</p>
          <div className="hero-cta">
            <a href="#enquiry" className="btn-primary" data-testid="cta-apply">Apply with our help</a>
            <Link to="/quiz" className="btn-link" data-testid="cta-quiz">Not sure where? Take the quiz →</Link>
          </div>
          <div className="hero-micro">
            <div><span className="hero-micro-dot"></span>40+ countries covered</div>
            <div><span className="hero-micro-dot"></span>Flat fee, no hidden costs</div>
            <div><span className="hero-micro-dot"></span>Human expert, not a chatbot</div>
          </div>
        </div>

        <Link to={`/countries/${heroCountry.slug}`} className="hero-visual reveal" data-testid="hero-visual" aria-label={`${heroCountry.name} ${heroCountry.visaName}`}>
          <img src={heroImage} alt={`${heroCountry.name} — ${heroCountry.visaName}`} />
          {/* Mobile-only overlay that carries the headline ON the image so they feel unified */}
          <div className="hero-overlay" aria-hidden="true">
            <div className="eyebrow">Digital Nomad Visa Assistance</div>
            <h2 className="hero-title-overlay">Moving abroad is complex. <em>Your visa shouldn’t be.</em></h2>
          </div>
          <div className="hero-card" data-testid="hero-card">
            <div className="hero-card-title"><span className="hero-card-flag" aria-hidden="true">{heroCountry.flag}</span>{heroCountry.name} — {heroCountry.visaName.replace(`${heroCountry.name} `, '').replace('Digital Nomad Visa', 'DNV')}</div>
            <div className="hero-card-row"><span className="hero-card-label">Income req.</span><span className="hero-card-val">{formatUSD(heroCountry.minIncome)}/mo</span></div>
            <div className="hero-card-row"><span className="hero-card-label">Processing</span><span className="hero-card-val">{heroCountry.processing}</span></div>
            <div className="hero-card-row"><span className="hero-card-label">Valid for</span><span className="hero-card-val">{heroCountry.validity}{heroCountry.renewable ? ', renewable' : ''}</span></div>
            <span className="hero-card-link">Explore {heroCountry.name} →</span>
          </div>
        </Link>
      </section>

      <section className="section countries-section" id="countries" style={{ paddingTop: 100 }}>
        <div className="section-inner">
          <div className="section-head reveal">
            <div className="section-eyebrow">Countries we file for</div>
            <h2 className="section-title">40+ programmes,<br/><em>one source of truth.</em></h2>
            <p className="section-sub">Real numbers, current as of this year, pulled from official government sources. Click any country to see income thresholds, tax implications, and processing timelines.</p>
          </div>
          <div className="countries-filters reveal">
            {REGION_FILTERS.map(f => (
              <button key={f.key} onClick={() => setRegion(f.key)} className={`filter-chip ${region === f.key ? 'active' : ''}`} data-testid={`filter-${f.key}`}>{f.label}</button>
            ))}
          </div>
          <div className="countries-grid">
            {featured.map(c => (<CountryCard key={c.slug} country={c} />))}
          </div>
          <div className="countries-cta reveal">
            <Link to="/countries" className="btn-link" data-testid="view-all-countries">View all 40 countries →</Link>
          </div>
        </div>
      </section>

      <section className="why-section" id="why">
        <div className="why-grid">
          <div className="why-image reveal">
            <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1000&q=80&auto=format&fit=crop" alt="A woman at her desk writing" />
          </div>
          <div className="why-text reveal">
            <div className="section-eyebrow">Why Nomad Compass</div>
            <h2>We’re not a directory. We’re the <em>people who file your papers.</em></h2>
            <p>Most nomad websites stop at telling you what a visa requires. We start there. The difference is whether you’re reading a blog post at 11pm trying to decode an embassy PDF — or whether you have a named human handling it for you.</p>
            <p>Sarah didn’t pay us to read more. She paid us so she could stop reading.</p>
            <div className="why-points">
              <div className="why-point"><div className="why-icon">01</div><div><strong>One flat fee, one expert</strong><span>No hourly billing surprises. You speak to the same person from first call to final approval.</span></div></div>
              <div className="why-point"><div className="why-icon">02</div><div><strong>Real data, not scraped blogs</strong><span>Income thresholds, tax angles, spouse rules — verified against government sources and refreshed quarterly.</span></div></div>
              <div className="why-point"><div className="why-icon">03</div><div><strong>Family-aware advice</strong><span>Most guides assume you’re single. We don’t. Spouse income thresholds, school options, dependent visas — all part of the conversation.</span></div></div>
              <div className="why-point"><div className="why-icon">04</div><div><strong>We say no when the answer is no</strong><span>If a visa won’t work for your situation, we tell you before you pay. We’d rather lose the sale than cost you a rejected application.</span></div></div>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section" id="faq">
        <div className="faq-wrap">
          <div className="section-head reveal">
            <div className="section-eyebrow">Questions we get asked</div>
            <h2 className="section-title">Answers before<br/><em>you even ask.</em></h2>
          </div>
          {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
        </div>
      </section>

      <section className="journal-teaser" id="journal">
        <div className="journal-teaser-inner">
          <div className="section-head reveal" style={{ marginBottom: 56 }}>
            <div className="section-eyebrow">From the Journal</div>
            <h2 className="section-title">Essays &amp; playbooks<br/><em>worth your time.</em></h2>
            <p className="section-sub">Honest, data-backed writing on digital nomad visas and the structural trends reshaping remote work.</p>
          </div>
          <div className="blog-grid">
            {latestPosts(3).map(p => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-card reveal" data-testid={`home-post-${p.slug}`}>
                <div className="blog-card-image"><img src={p.heroImage} alt={p.title} loading="lazy" /></div>
                <div className="blog-card-body">
                  <div className="blog-meta">
                    <span className="blog-pill">{p.category}</span>
                    <span>{formatDate(p.publishedAt)}</span>
                    <span>{p.readMinutes} min</span>
                  </div>
                  <h4>{p.title}</h4>
                  <p>{p.excerpt}</p>
                  <span className="blog-readmore">Read →</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="countries-cta reveal" style={{ marginTop: 48 }}>
            <Link to="/blog" className="btn-link" data-testid="view-all-posts">Read the full journal →</Link>
          </div>
        </div>
      </section>

      <section className="enquiry-section" id="enquiry">
        <div className="enq-grid">
          <div className="enq-left reveal">
            <div className="section-eyebrow">Start with a conversation</div>
            <h2>Tell us where<br/>you are. We’ll tell<br/>you <em>what we think.</em></h2>
            <p>Fill in a few details and we’ll reply within one working day with a realistic assessment — which visas you qualify for, which we’d recommend, and what it would cost.</p>
            <p>The first call is free and there’s no obligation. We’d rather earn your business than trap you in a funnel.</p>
            <div className="enq-promises">
              <div className="enq-promise">Your information is never shared with third parties.</div>
              <div className="enq-promise">We reply personally — no auto-responders.</div>
              <div className="enq-promise">If we can’t help, we’ll tell you who can.</div>
            </div>
          </div>
          <EnquiryForm pageSource="home" variant="home" />
        </div>
      </section>

      <Footer />
    </RevealProvider>
  );
}
